import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  ViewChild,
  signal,
  inject,
  OnInit,
  OnDestroy,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';

import { RouterModule } from '@angular/router';
import {
  MonitoringResultsStateService,
  type MonitResultSelectedRowId,
} from '../../../../shared/services/monitoring-results-state.service';
import { TypeSafeMatCellDef } from '../../../../type-safe-mat-cell-def.directive';
import { MonitoringResultItem } from '../../../../shared/types/monitoring-results.types';
import { UserService } from '../../../../shared/services/user.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
  selector: 'app-table-monitoring-safra-results',
  imports: [
    MatTableModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatSortModule,
    RouterModule,
    TypeSafeMatCellDef,
    MatTooltipModule,
    MatProgressBarModule,
  ],
  templateUrl: './table-monitoring-safra-results.component.html',
  styleUrl: './table-monitoring-safra-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableMonitoringSafraResultsComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  private readonly stateService = inject(MonitoringResultsStateService);
  private readonly userService = inject(UserService);
  private readonly destroy$ = new Subject<void>();

  readonly selectedRowId = signal<MonitResultSelectedRowId | null>(null);
  displayedColumns: string[] = [
    'mapa',
    'aviso',
    'alerta',
    'area_monitorada',
    'area_cultura',
    'em_desenvolvimento',
    'em_senescencia',
    'apto_para_colheita',
    'area_colhida',
    'cultura_divergente',
    'nao_plantado',
    'aguardando_identificacao',
    'possivel_cultura',
    'zarc',
    'idArea',
    'nome_fazenda',
    'produtor',
    'cpf_cnpj',
    'uf',
    'municipio',
    'car',
    'contrato',
    'apolice',
  ];

  readonly dataSource = new MatTableDataSource<MonitoringResultItem>([]);

  @Input()
  set data(value: MonitoringResultItem[]) {
    this.dataSource.data = value ?? [];
  }

  @Input() loading = false;

  @Input() currentFilters: Record<string, any> = {};
  @Input() currentPage = 0;
  @Input() currentPageSize = 10;
  @Input() currentSort: Sort = { active: 'municipio', direction: 'asc' };
  @Input() cultures: {
    id: number;
    description: string;
    enable: boolean;
  }[] = [];
  @Input() targetCulture: string | null = null;

  @ViewChild('tableContainer') tableContainer!: ElementRef<HTMLDivElement>;
  @ViewChild(MatSort) sort!: MatSort;

  @Output() sortChange = new EventEmitter<Sort>();

  private isDown = false;
  private startX = 0;
  private scrollLeft = 0;

  ngOnInit(): void {
    // Adiciona coluna cliente se for admin
    if (this.userService.isGeoambiente) {
      this.displayedColumns.push('cliente');
    }

    // Observa mudanças na linha selecionada
    this.stateService.selectedRowId$
      .pipe(takeUntil(this.destroy$))
      .subscribe((rowId) => {
        this.selectedRowId.set(rowId);

        // Limpa a seleção após o tempo da animação (10 segundos)
        if (rowId !== null) {
          setTimeout(() => {
            this.stateService.clearSelection();
          }, 20000);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Atualiza o MatSort quando currentSort mudar
    if (
      changes['currentSort'] &&
      this.sort &&
      !changes['currentSort'].firstChange
    ) {
      const newSort = changes['currentSort'].currentValue as Sort;
      if (newSort) {
        this.sort.active = newSort.active || 'municipio';
        this.sort.direction = newSort.direction || 'asc';
      }
    }

    // Quando os dados mudam e há uma linha selecionada, faz scroll até ela
    if (changes['data'] && this.selectedRowId()) {
      // Aguarda o próximo ciclo de renderização para garantir que a tabela foi atualizada
      setTimeout(() => {
        this.scrollToSelectedRow();
      }, 100);
    }
  }

  private scrollToSelectedRow(): void {
    const selectedRow = this.selectedRowId();
    if (!selectedRow) return;

    // Encontra o elemento tr com a classe selected-row
    const container = this.tableContainer?.nativeElement;
    if (!container) return;

    const selectedElement = container.querySelector(
      'tr.selected-row',
    ) as HTMLElement;
    if (selectedElement) {
      // Faz scroll suave até o elemento
      selectedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  }

  ngAfterViewInit() {
    // Configurar ordenação
    if (this.sort) {
      this.dataSource.sort = this.sort;
      // Aplica ordenação do estado atual (vindo do query param ou padrão)
      this.sort.active = this.currentSort.active || 'municipio';
      this.sort.direction = this.currentSort.direction || 'asc';
    }

    const container = this.tableContainer.nativeElement;

    container.addEventListener('mousedown', (e: any) => {
      this.isDown = true;
      container.classList.add('dragging');
      this.startX = e.pageX - container.offsetLeft;
      this.scrollLeft = container.scrollLeft;
    });

    container.addEventListener('mouseleave', () => {
      this.isDown = false;
      container.classList.remove('dragging');
    });

    container.addEventListener('mouseup', () => {
      this.isDown = false;
      container.classList.remove('dragging');
    });

    container.addEventListener('mousemove', (e: any) => {
      if (!this.isDown) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - this.startX) * 1; // *1 é a velocidade do scroll
      container.scrollLeft = this.scrollLeft - walk;
    });
  }

  onSortChange(event: Sort) {
    this.sortChange.emit(event);
  }

  protected formatDecimal(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '—';
    }

    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  /**
   * Verifica se a linha é a que foi selecionada anteriormente
   */
  isSelectedRow(row: MonitoringResultItem): boolean {
    return (
      this.selectedRowId()?.areaId === row.areaId &&
      this.selectedRowId()?.requestId === row.requestId
    );
  }

  /**
   * Obtém o nome da cultura pelo ID
   */
  public getCultureNameById(cultureId: number | null): string {
    if (!cultureId) return 'Cultura não identificada';

    const culture = this.cultures.find((c) => c.id === cultureId);
    return culture?.description || 'Cultura não identificada';
  }

  /**
   * Gera os queryParams para navegação, incluindo todos os filtros atuais
   */
  getNavigationQueryParams(element: MonitoringResultItem): Record<string, any> {
    return {
      cropYear: element.cropYear,
      cultureId: element.cultureId,
      cultureName: this.getCultureNameById(element.cultureId),
      monitoringAreaIds: element.monitoringAreaIds.join(','), // lista serializada
      ...this.currentFilters,
      page: this.currentPage,
      pageSize: this.currentPageSize,
      sortBy: this.sort.active || undefined,
      sortDirection: this.sort.direction || undefined,
    };
  }

  /**
   * Chamado quando o usuário clica no ícone do mapa para ver detalhes
   */
  onNavigateToDetails(element: MonitoringResultItem): void {
    const { areaId, requestId } = element;
    this.stateService.setSelectedRowId(areaId ? { areaId, requestId } : null);
  }
}
